import React, { useState } from 'react';
import {
  setLocationDefaultLanguage,
  SUPPORTED_LANGUAGES,
} from '../../../services/translationsAdminService';

/**
 * Per-location default-language editor (feat-018-learn-i18n Phase 2, ADR 0004 D2).
 *
 * Sets `location.default_language`, the third tier of the resolution stack
 * (user.preferred_locale → location.default_language → tenant.default_language →
 * 'en', ADR 0001 D7). Wired to the existing PUT /api/tenant/{tid}/locations/{lid}
 * extended with a `default_language` field. Persistence ships in the Phase 2
 * backend slice; until then saves are silently ignored by the handler.
 *
 * RBAC: rendered only when the parent grants `canManage` (StaffRoute +
 * tenant-scoped corporate/admin). Note (ADR 0004 D2): store managers cannot set
 * a location default — corporate/admin only — by design.
 */
const LocationLanguageDefaultCard: React.FC<{
  tenantId: string;
  locationId: string;
  initialLanguage?: string;
  canManage: boolean;
}> = ({ tenantId, locationId, initialLanguage, canManage }) => {
  const [lang, setLang] = useState<string>(initialLanguage ?? 'en');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setNote(null);
    try {
      await setLocationDefaultLanguage(tenantId, locationId, lang);
      setNote('Saved.');
    } catch (e) {
      setNote(`Could not save: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-6 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="location-language-default-card"
    >
      <h2 className="text-lg font-semibold text-ink mb-1">Default language</h2>
      <p className="text-sm text-inkMuted mb-3">
        The language trainees at this location see when they haven't set their own
        preference. Falls back to the tenant default, then English.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="text-sm border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          disabled={!canManage || saving}
          data-testid="location-default-language-select"
          aria-label="Location default language"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={!canManage || saving}
          onClick={handleSave}
          data-testid="location-default-language-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {note && <span className="text-xs text-inkMuted">{note}</span>}
      </div>
      {!canManage && (
        <p className="mt-2 text-xs text-inkMuted">
          Only corporate or admin users can change the location default language.
        </p>
      )}
    </div>
  );
};

export default LocationLanguageDefaultCard;
